import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useState } from "react";
import { DashboardShell } from "@/components/DashboardShell";
import { ListSkeleton } from "@/components/PageSkeleton";
import {
  getPipeline,
  listCustomers,
  upsertContact,
  type CustomerSummary,
} from "@/lib/crm.functions";
import { CustomerRecordView } from "@/components/CustomerRecordView";
import { updateLeadStatus } from "@/lib/leads.functions";
import {
  Users,
  Inbox,
  Handshake,
  CalendarDays,
  Flame,
  Banknote,
  TrendingUp,
  Search,
  ChevronRight,
  Plus,
  UserPlus,
} from "lucide-react";
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import { useHydratedTranslation } from "@/components/marketing/Layout";
import { buildSeo } from "@/lib/seo";

const LEAD_STATUSES = ["new", "hot", "warm", "cold", "won", "lost"] as const;

export const Route = createFileRoute("/_authenticated/crm")({
  validateSearch: (s: Record<string, unknown>): { c?: string } => ({
    c: typeof s.c === "string" && s.c ? s.c : undefined,
  }),
  head: () =>
    buildSeo({
      path: "/crm",
      title: "CRM & Orders — Reality Twin",
      description:
        "A real CRM: every customer as a full record — conversations, leads, appointments, orders, revenue and notes — plus one pipeline for every commercial relationship.",
      noindex: true,
    }),
  component: Page,
});

const KIND_META: Record<string, { icon: typeof Users; to: string; hue: string }> = {
  lead: { icon: Users, to: "/leads", hue: "60" },
  order: { icon: Inbox, to: "/requests", hue: "168" },
  deal: { icon: Handshake, to: "/deals", hue: "290" },
  appointment: { icon: CalendarDays, to: "/appointments", hue: "240" },
};

const ORDER_STAGE_KEY: Record<string, string> = {
  new: "stNew",
  quoted: "stQuoted",
  paid: "stPaid",
  completed: "stCompleted",
  declined: "stDeclined",
};

const STATUS_HUE: Record<string, string> = {
  hot: "25",
  warm: "60",
  cold: "240",
  won: "150",
  lost: "10",
  new: "200",
  customer: "168",
  contact: "260",
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("") || "?";
}

function Page() {
  const t = useHydratedTranslation();
  const navigate = useNavigate();
  const { c: selectedEmail } = Route.useSearch();
  const fetchPipeline = useServerFn(getPipeline);
  const fetchCustomers = useServerFn(listCustomers);
  const setLeadStatus = useServerFn(updateLeadStatus);
  const addContact = useServerFn(upsertContact);
  const [adding, setAdding] = useState(false);
  const [newC, setNewC] = useState({ email: "", name: "" });
  const [addBusy, setAddBusy] = useState(false);

  async function submitNewCustomer(e: React.FormEvent) {
    e.preventDefault();
    const email = newC.email.trim();
    if (!email || addBusy) return;
    setAddBusy(true);
    try {
      await addContact({ data: { email, name: newC.name.trim() || undefined } });
      setAdding(false);
      setNewC({ email: "", name: "" });
      navigate({ to: "/crm", search: { c: email } });
    } catch (err: any) {
      toast.error(err?.message ?? "Failed");
    } finally {
      setAddBusy(false);
    }
  }
  const [data, setData] = useState<{ items: any[]; kpis: any; revenueSeries?: any[] } | null>(null);
  const [customers, setCustomers] = useState<CustomerSummary[] | null>(null);
  const [view, setView] = useState<"customers" | "pipeline">("customers");
  const [filter, setFilter] = useState<string>("all");
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (selectedEmail) return; // viewing a single record — no list needed
    fetchPipeline()
      .then((r) => setData(r as any))
      .catch(() => setData({ items: [], kpis: {} }));
    fetchCustomers()
      .then((r) => setCustomers((r as any).customers ?? []))
      .catch(() => setCustomers([]));
  }, [fetchPipeline, fetchCustomers, selectedEmail]);

  async function changeLeadStatus(itemId: string, status: string) {
    const leadId = itemId.replace(/^lead:/, "");
    setData((d) =>
      d ? { ...d, items: d.items.map((x) => (x.id === itemId ? { ...x, stage: status } : x)) } : d,
    );
    try {
      await setLeadStatus({ data: { id: leadId, status: status as any } });
      toast.success(t("app.pages.leads.statusUpdated"));
    } catch (e: any) {
      toast.error(e?.message ?? "Failed");
    }
  }

  const items = useMemo(
    () => (data?.items ?? []).filter((i) => filter === "all" || i.kind === filter),
    [data, filter],
  );
  const filteredCustomers = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = customers ?? [];
    if (!q) return list;
    return list.filter(
      (c) => c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q),
    );
  }, [customers, query]);
  const k = data?.kpis ?? {};

  function statusLabel(status: string): string {
    if (status === "customer") return t("app.pages.crm.stCustomer", { defaultValue: "Customer" });
    if (status === "contact") return t("app.pages.crm.stContact", { defaultValue: "Contact" });
    return t(`app.pages.status.${status}`, { defaultValue: status });
  }

  function stageLabel(kind: string, stage: string): string {
    if (kind === "lead") return t(`app.pages.status.${stage}`, { defaultValue: stage });
    if (kind === "order")
      return t(`app.pages.requests.${ORDER_STAGE_KEY[stage] ?? "stNew"}`, { defaultValue: stage });
    if (kind === "deal") return t(`app.pages.deals.st_${stage}`, { defaultValue: stage });
    if (kind === "appointment") {
      if (stage === "completed") return t("app.pages.appointments.statusCompleted");
      if (stage === "cancelled") return t("app.pages.appointments.statusCancelled");
      return t("app.pages.crm.scheduled");
    }
    return stage;
  }

  const kpis = [
    { key: "leads", label: t("app.pages.crm.kLeads"), value: k.leads ?? 0, icon: Users },
    { key: "hot", label: t("app.pages.crm.kHot"), value: k.hotLeads ?? 0, icon: Flame },
    { key: "open", label: t("app.pages.crm.kOpenOrders"), value: k.openOrders ?? 0, icon: Inbox },
    {
      key: "rev",
      label: t("app.pages.crm.kRevenue"),
      value: `${(k.revenue ?? 0).toLocaleString()} ${k.currency ?? ""}`,
      icon: Banknote,
    },
    {
      key: "payout",
      label: t("app.pages.crm.kPayout"),
      value: `${(k.payout ?? 0).toLocaleString()} ${k.currency ?? ""}`,
      icon: Banknote,
    },
    { key: "deals", label: t("app.pages.crm.kDeals"), value: k.activeDeals ?? 0, icon: Handshake },
    {
      key: "pipe",
      label: t("app.pages.crm.kPipeline"),
      value: `${(k.pipelineValue ?? 0).toLocaleString()} ${k.currency ?? ""}`,
      icon: TrendingUp,
    },
  ];

  const filters: { id: string; label: string }[] = [
    { id: "all", label: t("app.pages.crm.all") },
    { id: "lead", label: t("app.pages.crm.fLead") },
    { id: "order", label: t("app.pages.crm.fOrder") },
    { id: "deal", label: t("app.pages.crm.fDeal") },
    { id: "appointment", label: t("app.pages.crm.fAppt") },
  ];

  // A customer is selected → show the full record (Kundenakte).
  if (selectedEmail) {
    return (
      <DashboardShell title={t("app.pages.crm.title")} subtitle={t("app.pages.crm.subtitle")}>
        <CustomerRecordView
          email={selectedEmail}
          onBack={() => navigate({ to: "/crm", search: {} })}
        />
      </DashboardShell>
    );
  }

  return (
    <DashboardShell title={t("app.pages.crm.title")} subtitle={t("app.pages.crm.subtitle")}>
      {!data || customers === null ? (
        <ListSkeleton rows={5} />
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 mb-6">
            {kpis.map((kpi) => (
              <div key={kpi.key} className="glass-strong rounded-2xl p-4">
                <kpi.icon className="h-4 w-4 text-muted-foreground" />
                <div className="font-display text-2xl font-semibold mt-2 truncate">{kpi.value}</div>
                <div className="text-[11px] text-muted-foreground mt-1">{kpi.label}</div>
              </div>
            ))}
          </div>

          {(data.revenueSeries ?? []).some((p: any) => p.revenue > 0) && (
            <div className="glass-strong rounded-2xl p-5 mb-6">
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono mb-3">
                {t("app.pages.crm.kRevenue")}
              </div>
              <div className="h-52">
                <ResponsiveContainer>
                  <AreaChart data={data.revenueSeries}>
                    <defs>
                      <linearGradient id="crmRev" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="oklch(0.82 0.18 168)" stopOpacity={0.6} />
                        <stop offset="100%" stopColor="oklch(0.82 0.18 168)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                    <XAxis
                      dataKey="label"
                      stroke="oklch(0.65 0.02 260)"
                      fontSize={11}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      stroke="oklch(0.65 0.02 260)"
                      fontSize={11}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "#0F1117",
                        border: "1px solid rgba(255,255,255,0.1)",
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      stroke="oklch(0.82 0.18 168)"
                      strokeWidth={2}
                      fill="url(#crmRev)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* View toggle: real customer records vs the flat pipeline */}
          <div className="mb-4 flex items-center gap-2">
            <div className="inline-flex rounded-full glass border border-border p-0.5">
              {(["customers", "pipeline"] as const).map((v) => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  className={`text-xs px-4 py-1.5 rounded-full transition ${
                    view === v
                      ? "brand-gradient text-black"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {v === "customers"
                    ? t("app.pages.crm.viewCustomers", { defaultValue: "Customers" })
                    : t("app.pages.crm.viewPipeline", { defaultValue: "Pipeline" })}
                </button>
              ))}
            </div>
            {view === "customers" && (
              <span className="text-xs text-muted-foreground">{(customers ?? []).length}</span>
            )}
          </div>

          {view === "customers" ? (
            <>
              <div className="flex gap-2 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t("app.pages.crm.search", { defaultValue: "Search customers…" })}
                    className="w-full rounded-xl glass border border-border pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]"
                  />
                </div>
                <button
                  onClick={() => setAdding((v) => !v)}
                  className="inline-flex items-center gap-1.5 rounded-xl brand-gradient text-black px-4 text-sm font-medium shrink-0"
                >
                  <UserPlus className="h-4 w-4" />
                  <span className="hidden sm:inline">
                    {t("app.pages.crm.addCustomer", { defaultValue: "Add customer" })}
                  </span>
                </button>
              </div>
              {adding && (
                <form
                  onSubmit={submitNewCustomer}
                  className="glass-strong rounded-2xl p-4 mb-4 flex flex-col sm:flex-row gap-2"
                >
                  <input
                    required
                    type="email"
                    value={newC.email}
                    onChange={(e) => setNewC({ ...newC, email: e.target.value })}
                    placeholder={t("app.pages.crm.fEmail", { defaultValue: "Email" })}
                    className="flex-1 rounded-lg bg-white/5 border border-border px-3 py-2 text-sm focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]"
                  />
                  <input
                    value={newC.name}
                    onChange={(e) => setNewC({ ...newC, name: e.target.value })}
                    placeholder={t("app.pages.crm.fName", { defaultValue: "Name" })}
                    className="flex-1 rounded-lg bg-white/5 border border-border px-3 py-2 text-sm focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]"
                  />
                  <button
                    type="submit"
                    disabled={addBusy}
                    className="rounded-lg brand-gradient text-black px-4 py-2 text-sm font-medium disabled:opacity-50 shrink-0"
                  >
                    <Plus className="h-4 w-4 inline" />{" "}
                    {t("app.pages.crm.addCta", { defaultValue: "Add" })}
                  </button>
                </form>
              )}
              {filteredCustomers.length === 0 ? (
                <div className="glass-strong rounded-2xl p-12 text-center text-sm text-muted-foreground">
                  {query
                    ? t("app.pages.crm.noMatch", {
                        defaultValue: "No customers match your search.",
                      })
                    : t("app.pages.crm.noCustomers", {
                        defaultValue:
                          "No customers yet. They appear here as your twin captures conversations, leads, appointments and orders.",
                      })}
                </div>
              ) : (
                <div className="glass-strong rounded-2xl divide-y divide-border overflow-hidden">
                  {filteredCustomers.map((c) => {
                    const hue = STATUS_HUE[c.status] ?? "260";
                    const bits: string[] = [];
                    if (c.conversations)
                      bits.push(
                        `${c.conversations} ${t("app.pages.crm.cConversations", { defaultValue: "conversations" })}`,
                      );
                    if (c.leads)
                      bits.push(
                        `${c.leads} ${t("app.pages.crm.cLeads", { defaultValue: "leads" })}`,
                      );
                    if (c.appointments)
                      bits.push(
                        `${c.appointments} ${t("app.pages.crm.cAppointments", { defaultValue: "appointments" })}`,
                      );
                    if (c.orders)
                      bits.push(
                        `${c.orders} ${t("app.pages.crm.cOrders", { defaultValue: "orders" })}`,
                      );
                    return (
                      <Link
                        key={c.email}
                        to="/crm"
                        search={{ c: c.email }}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition"
                      >
                        <span
                          className="h-9 w-9 shrink-0 rounded-full flex items-center justify-center text-[12px] font-semibold"
                          style={{
                            background: `oklch(0.82 0.15 ${hue} / 0.12)`,
                            color: `oklch(0.82 0.15 ${hue})`,
                          }}
                        >
                          {initials(c.name)}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate">{c.name}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {bits.join(" · ") || c.email}
                          </div>
                        </div>
                        {c.revenue > 0 && (
                          <div className="text-sm font-mono shrink-0 hidden sm:block">
                            {c.revenue.toLocaleString()} {c.currency}
                          </div>
                        )}
                        <span
                          className="shrink-0 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-md glass"
                          style={{ color: `oklch(0.82 0.15 ${hue})` }}
                        >
                          {statusLabel(c.status)}
                        </span>
                        <span className="shrink-0 text-[11px] text-muted-foreground hidden md:block w-20 text-right">
                          {new Date(c.lastActivity).toLocaleDateString()}
                        </span>
                        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                      </Link>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="mb-4 flex gap-2 flex-wrap">
                {filters.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setFilter(f.id)}
                    className={`text-xs px-3 py-1.5 rounded-full border ${
                      filter === f.id
                        ? "brand-gradient text-black border-transparent"
                        : "glass border-border text-muted-foreground hover:bg-white/5"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              {items.length === 0 ? (
                <div className="glass-strong rounded-2xl p-12 text-center text-sm text-muted-foreground">
                  {t("app.pages.crm.empty")}
                </div>
              ) : (
                <div className="glass-strong rounded-2xl divide-y divide-border overflow-hidden">
                  {items.map((it) => {
                    const meta = KIND_META[it.kind] ?? KIND_META.lead;
                    const Icon = meta.icon;
                    return (
                      <div
                        key={it.id}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition"
                      >
                        <Link to={meta.to} className="flex items-center gap-3 min-w-0 flex-1">
                          <span
                            className="h-8 w-8 shrink-0 rounded-full flex items-center justify-center"
                            style={{
                              background: `oklch(0.82 0.15 ${meta.hue} / 0.12)`,
                              color: `oklch(0.82 0.15 ${meta.hue})`,
                            }}
                          >
                            <Icon className="h-4 w-4" />
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium truncate">{it.title}</div>
                            {it.subtitle && (
                              <div className="text-xs text-muted-foreground truncate">
                                {it.subtitle}
                              </div>
                            )}
                          </div>
                        </Link>
                        {it.value != null && (
                          <div className="text-sm font-mono shrink-0 hidden sm:block">
                            {Number(it.value).toLocaleString()} {it.currency}
                          </div>
                        )}
                        {it.kind === "lead" ? (
                          <select
                            value={it.stage}
                            onChange={(e) => changeLeadStatus(it.id, e.target.value)}
                            className="shrink-0 text-[11px] rounded-md bg-white/5 border border-border px-2 py-1 focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]"
                          >
                            {LEAD_STATUSES.map((s) => (
                              <option key={s} value={s}>
                                {t(`app.pages.status.${s}`, { defaultValue: s })}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span
                            className="shrink-0 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-md glass"
                            style={{ color: `oklch(0.82 0.15 ${meta.hue})` }}
                          >
                            {stageLabel(it.kind, it.stage)}
                          </span>
                        )}
                        <span className="shrink-0 text-[11px] text-muted-foreground hidden md:block w-20 text-right">
                          {new Date(it.date).toLocaleDateString()}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </>
      )}
    </DashboardShell>
  );
}
